const VOLTAGE_UNIT = "V";
const TEMP_UNIT = "°C";

const FAULT_LEVEL_MAP = {
    "normal": 0,
    "abnormal 1": 1,
    "abnormal 2": 2,
    "abnormal 3": 3,
    "abnormal 4": 4,
    "abnormal 5": 5,
};

const FAULT_LABEL_MAP = Object.fromEntries(
    Object.entries(FAULT_LEVEL_MAP).map(([label, level]) => [level, label])
);

function isBmsFaultAlert(value) {
    return ![null, undefined, "normal"].includes(value);
}

function formatValue(value, unit) {
    return value != null ? `${value} ${unit}` : "-";
}

function computeDelta(max, min) {
    const delta = max - min;
    if (isNaN(delta)) return { value: "-", isAlert: false };
    return { value: `${parseFloat(delta.toFixed(4))}`, isAlert: delta !== 0 };
}

function parseFaultLevel(value) {
    if (value == null) return 0;
    const lower = String(value).toLowerCase();
    return FAULT_LEVEL_MAP[lower] ?? 0;
}

function faultLevelToLabel(level) {
    return FAULT_LABEL_MAP[level] ?? "normal";
}

function buildRow(metricLabel, getVal, getHighlight, systemVal, systemHighlight, stringCols, perString) {
    const values = { Metric: metricLabel };
    const highlighted = { Metric: false };
    stringCols.forEach((col, i) => {
        values[col] = getVal(perString[i]);
        highlighted[col] = getHighlight(perString[i]);
    });
    values["SYSTEM"] = systemVal;
    highlighted["SYSTEM"] = systemHighlight;
    return { values, highlighted };
}

export function buildStringMetricsTableData(stringsData) {
    if (!Array.isArray(stringsData) || stringsData.length === 0) {
        return { columns: [], rows: [] };
    }

    const sorted = [...stringsData].sort((a, b) => (a.stringNum ?? 0) - (b.stringNum ?? 0));

    const stringCols = sorted.map((s) => `STRING ${s.stringNum}`);
    const columns = ["Metric", ...stringCols, "SYSTEM"];

    // Per-string computed values
    const perString = sorted.map((s) => {
        const faultRaw = s.ext_highest_fault_level_string;
        const faultAlert = isBmsFaultAlert(faultRaw);
        const faultLevel = faultRaw ?? "-";

        const minV = s.stringMinVoltageCell;
        const maxV = s.stringMaxVoltageCell;
        const deltaV = computeDelta(maxV, minV);

        const minT = s.stringMinTempCell;
        const maxT = s.stringMaxTempCell;
        const deltaT = computeDelta(maxT, minT);

        return { faultLevel, faultAlert, minV, maxV, deltaV, minT, maxT, deltaT };
    });

    // SYSTEM aggregation
    const allFaultLevels = sorted.map((s) => parseFaultLevel(s.ext_highest_fault_level_string));
    const systemFaultLevel = allFaultLevels.reduce((max, v) => Math.max(max, v), 0);
    const systemFaultAlert = sorted.some((s) => isBmsFaultAlert(s.ext_highest_fault_level_string));
    const allFaultsNull = sorted.every((s) => s.ext_highest_fault_level_string == null);

    const validMinV = sorted.map((s) => s.stringMinVoltageCell).filter((v) => v != null);
    const systemMinV = validMinV.length ? Math.min(...validMinV) : null;

    const validMaxV = sorted.map((s) => s.stringMaxVoltageCell).filter((v) => v != null);
    const systemMaxV = validMaxV.length ? Math.max(...validMaxV) : null;

    const systemDeltaV = computeDelta(systemMaxV, systemMinV);

    const validMinT = sorted.map((s) => s.stringMinTempCell).filter((v) => v != null);
    const systemMinT = validMinT.length ? Math.min(...validMinT) : null;

    const validMaxT = sorted.map((s) => s.stringMaxTempCell).filter((v) => v != null);
    const systemMaxT = validMaxT.length ? Math.max(...validMaxT) : null;

    const systemDeltaT = computeDelta(systemMaxT, systemMinT);

    const rows = [
        buildRow(
            "Highest Battery System Fault Level :",
            (p) => p.faultLevel,
            (p) => p.faultAlert,
            allFaultsNull ? "-" : faultLevelToLabel(systemFaultLevel),
            systemFaultAlert,
            stringCols, perString,
        ),
        buildRow(
            "Minimum Cell Voltage :",
            (p) => formatValue(p.minV, VOLTAGE_UNIT),
            () => false,
            formatValue(systemMinV, VOLTAGE_UNIT),
            false,
            stringCols, perString,
        ),
        buildRow(
            "Maximum Cell Voltage :",
            (p) => formatValue(p.maxV, VOLTAGE_UNIT),
            () => false,
            formatValue(systemMaxV, VOLTAGE_UNIT),
            false,
            stringCols, perString,
        ),
        buildRow(
            "Delta Cell Voltage :",
            (p) => p.deltaV.isAlert ? `${p.deltaV.value} ${VOLTAGE_UNIT}` : "-",
            () => false,
            "-",
            false,
            stringCols, perString,
        ),
        buildRow(
            "Minimum Cell Temperature :",
            (p) => formatValue(p.minT, TEMP_UNIT),
            () => false,
            formatValue(systemMinT, TEMP_UNIT),
            false,
            stringCols, perString,
        ),
        buildRow(
            "Maximum Cell Temperature :",
            (p) => formatValue(p.maxT, TEMP_UNIT),
            () => false,
            formatValue(systemMaxT, TEMP_UNIT),
            false,
            stringCols, perString,
        ),
        buildRow(
            "Delta Cell Temperature :",
            (p) => p.deltaT.isAlert ? `${p.deltaT.value} ${TEMP_UNIT}` : "-",
            () => false,
            systemDeltaT.isAlert ? `${systemDeltaT.value} ${TEMP_UNIT}` : "-",
            false,
            stringCols, perString,
        ),
    ];

    return { columns, rows };
}
